/**
 * Thumbnails panel (F-130) — the working surface for a video's YouTube
 * thumbnails: who is in them, what they say, which models make them, the run,
 * and the gallery where each frame can be refined, picked and put on the board.
 *
 * A PANEL, not a modal, like the design theme: the gallery is looked at while
 * looking at the board, and a refinement round-trip is tens of seconds — a
 * dialog that had to stay open for that would be a hostage.
 *
 * ONE VERB PER THING. The panel's primary action is "Generate five
 * thumbnails"; everything above it is what the run reads (faces, words,
 * models) and everything below it is what the run made. Per frame there are
 * three quiet actions — Use on board, Refine, Save — and nothing else;
 * "picked" is a consequence of "Use on board", not a fourth button.
 *
 * Words commit on BLUR (same reasoning as the design fields): two people
 * rarely type the same phrase at once, and per-keystroke writes would fight.
 *
 * Progress and the gallery are read from the DOC, not from this component:
 * the run is server-side and shared, so a teammate watching the same board
 * sees the same bar, and a reload mid-run still shows it.
 */
import {
  MAX_THUMB_PORTRAITS,
  THUMB_TEXT_MAX_LENGTH,
  THUMB_TITLE_MAX_LENGTH,
  THUMBNAIL_RUN_COUNT,
  type PresenterPhoto,
  type ThumbPortraitInfo,
  type ThumbShotInfo,
  type BoardSummary,
} from "@kola/shared";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useStore } from "zustand";

import {
  assetUrl,
  cancelThumbnails,
  dismissThumbnailsState,
  refineThumbnail,
  startThumbnails,
  uploadAsset,
} from "../../core/api.js";
import type { CanvasStore } from "../../core/canvas.js";
import type { BoardSession } from "../../core/session.js";
import { CloseButton } from "../../ui/CloseButton.js";
import { iconClose } from "../../ui/icons.js";
import { PanelResizeHandle } from "../../ui/PanelResizeHandle.js";
import { ProgressBar } from "../../ui/ProgressBar.js";
import { useDockedPanelWidth } from "../../ui/useDockedPanelWidth.js";
import { showToast } from "../../ui/toast.js";
import { optimizeForUpload } from "../elements/optimizeImage.js";
import { ModelSelect } from "../ai-models/ModelSelect.js";
import { AddPhotosButton, PresenterLibraryDialog } from "../presenter-photos/PresenterLibraryDialog.js";
import { useOfferedPresenterPhotos, usePresenterPhotos } from "../presenter-photos/store.js";
import { useLibraryUploads } from "../presenter-photos/uploads.js";
import { describeThumbnailError } from "./errors.js";
import { fallbackPlacement, hasViewport, thumbPlacement, waitForCanvas } from "./place.js";
import type { ThumbUiStore } from "./store.js";
import type { ThumbConceptColumn, ThumbRunGroup } from "./tree.js";
import { useThumbnails, type ThumbnailsView } from "./use-thumbnails.js";

export interface ThumbnailPanelProps {
  session: BoardSession;
  canvas: CanvasStore;
  ui: ThumbUiStore;
  /**
   * F-142: a Shorts week's board makes vertical COVER FRAMES, not
   * thumbnails — the panel says so, and the server renders 9:16 for it.
   */
  summary?: BoardSummary | null | undefined;
  /**
   * Show the Grid (the canvas) — called before a thumbnail is placed, so the
   * frame lands where the user can see it even when the board was open as the
   * script document. Absent in a host with no Script view.
   */
  onShowGrid?: (() => void) | undefined;
}

/** Hook text past this many characters stops reading as a hook. */
const TEXT_IDEAL_MAX = 8;

export function ThumbnailPanel({ session, canvas, ui, onShowGrid, summary }: ThumbnailPanelProps) {
  const view = useThumbnails(session);
  const panelWidth = useDockedPanelWidth("thumbnails", 380);
  const starting = useStore(ui, (s) => s.starting);
  const error = useStore(ui, (s) => s.error);
  const sheetId = useStore(session.store, (s) => s.sheetId);
  const job = view.job;
  const running = job?.status === "running";
  // F-134: with a tagged presenter library the run chooses its own face per
  // frame, so a board needs no portrait of its own to start.
  const library = useOfferedPresenterPhotos();
  const hasPortrait = library.length > 0 || view.portraits.length > 0;

  async function generate() {
    if (sheetId === null) return;
    ui.getState().setError(null);
    ui.getState().setStarting(true);
    try {
      await startThumbnails(session.boardId, sheetId, {
        imageModel: view.imageModel,
        textModel: view.textModel,
      });
    } catch (err) {
      ui.getState().setError(describeThumbnailError(err));
    } finally {
      ui.getState().setStarting(false);
    }
  }

  async function stop() {
    if (job === undefined) return;
    try {
      await cancelThumbnails(session.boardId, job.jobId);
    } catch (err) {
      ui.getState().setError(describeThumbnailError(err));
    }
  }

  async function dismiss() {
    try {
      await dismissThumbnailsState(session.boardId);
    } catch {
      // 409 = the run is still going and will republish; nothing to say.
    }
  }

  const cover = summary?.kind === "shorts";
  const noun = cover ? "cover frames" : "thumbnails";
  const title = cover ? "Cover frames" : "Thumbnails";

  return (
    <aside className="thumb-panel" aria-label={title} style={{ width: panelWidth.width }}>
      <PanelResizeHandle
        label={`Resize the ${noun} panel`}
        width={panelWidth.width}
        bounds={panelWidth.bounds}
        onResize={panelWidth.setWidth}
        onCommit={panelWidth.commit}
        onReset={panelWidth.reset}
      />
      <div className="thumb-panel-head">
        <span className="thumb-panel-title">{title}</span>
        <span className="spacer" />
        <CloseButton onClick={() => ui.getState().setPanelOpen(false)} label={`Close ${noun}`} />
      </div>

      <div className="thumb-panel-body">
        <PeopleSection session={session} ui={ui} portraits={view.portraits} library={library} />
        <WordsSection session={session} view={view} />
        <ModelsSection session={session} view={view} />

        <section className="thumb-section">
          {running && job !== undefined ? (
            <>
              <div className="thumb-run-row">
                <ProgressBar
                  percent={job.total === 0 ? 0 : Math.round((job.done / job.total) * 100)}
                  label={
                    job.phase === "writing"
                      ? "Writing the hooks…"
                      : `Rendering ${Math.min(job.done + 1, job.total)} of ${job.total}`
                  }
                />
                <button type="button" className="btn" onClick={() => void stop()}>
                  Stop
                </button>
              </div>
              <p className="thumb-hint">
                Each frame lands below as it finishes
                {job.failed > 0 ? ` · ${job.failed} failed` : ""}.
              </p>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-primary thumb-generate"
              onClick={() => void generate()}
              disabled={starting || !hasPortrait || sheetId === null}
            >
              {starting
                ? "Starting…"
                : view.shots.length > 0
                  ? `Generate ${THUMBNAIL_RUN_COUNT} more`
                  : `Generate ${THUMBNAIL_RUN_COUNT} ${noun}`}
            </button>
          )}
          {!hasPortrait && !running && (
            <p className="thumb-hint">
              Add a portrait above to start — or an admin adds presenter photos on /admin once,
              and every thumbnail picks its own.
            </p>
          )}
          {error !== null && (
            <p className="thumb-error" role="alert">
              {error}
            </p>
          )}
          {job !== undefined && !running && (
            <p className="thumb-provenance">
              {job.status === "cancelled" ? "Stopped" : job.status === "error" ? "Failed" : "Done"} —{" "}
              {job.status === "error" && job.message
                ? job.message
                : `${job.done - job.failed} of ${job.total} ${noun}${job.failed > 0 ? `, ${job.failed} failed` : ""}`}
              .{" "}
              <button type="button" className="thumb-link-btn" onClick={() => void dismiss()}>
                Dismiss
              </button>
            </p>
          )}
        </section>

        {view.runs.length === 0 ? (
          !running && (
            <div className="thumb-empty">
              {cover
                ? "Five vertical cover frames, each a different hook, built around the presenter and the Short's own look. Refine any of them with a sentence."
                : "Five thumbnails, each a different hook, built around the presenter and the video's own look. Refine any of them with a sentence."}
            </div>
          )
        ) : (
          <Gallery session={session} canvas={canvas} ui={ui} view={view} onShowGrid={onShowGrid} />
        )}
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/* People                                                              */
/* ------------------------------------------------------------------ */

/**
 * Who is in the frame. The presenter slot is the shared photo library
 * (F-134) — the run picks the photo whose expression fits each hook, and
 * anyone can add photos to it from here — so every portrait uploaded on the
 * board is a guest. Only while the library is still empty does the first
 * board upload stand in as the presenter, as it did before the library.
 */
function PeopleSection({
  session,
  ui,
  portraits,
  library,
}: {
  session: BoardSession;
  ui: ThumbUiStore;
  portraits: ThumbPortraitInfo[];
  library: readonly PresenterPhoto[];
}) {
  const [uploading, setUploading] = useState(false);
  const room = MAX_THUMB_PORTRAITS - portraits.length;
  const fromLibrary = library.length > 0;

  async function addFiles(files: readonly File[]) {
    if (files.length === 0) return;
    setUploading(true);
    ui.getState().setError(null);
    try {
      for (const file of files.slice(0, Math.max(room, 0))) {
        // Same path as a board image drop — cap the long edge and re-encode
        // before it crosses the wire (F-30's optimizeForUpload).
        const asset = await uploadAsset(session.boardId, await optimizeForUpload(file));
        session.addThumbPortrait(asset.assetId, asset.w, asset.h);
      }
    } catch (err) {
      ui.getState().setError(describeThumbnailError(err));
    } finally {
      setUploading(false);
    }
  }

  return (
    <section className="thumb-section">
      <div className="thumb-section-head">
        <span className="thumb-label">People</span>
        <p className="thumb-hint">
          {fromLibrary
            ? "The presenter comes from the shared photo library — AI picks the expression per thumbnail. Guests are optional."
            : "Add presenter photos to the shared library and AI picks one per thumbnail; a portrait uploaded here stands in until then."}
        </p>
      </div>
      <div className="thumb-people">
        <PresenterCard photos={library} />
        {portraits.map((portrait, index) => {
          const presenter = !fromLibrary && index === 0;
          return (
          <figure className={`thumb-person${presenter ? " presenter" : ""}`} key={portrait.id}>
            <div className="thumb-person-face">
              <img src={assetUrl(portrait.assetId)} alt={portrait.label || (presenter ? "Presenter" : "Guest")} />
            </div>
            <span className="thumb-person-role">{presenter ? "Presenter" : "Guest"}</span>
            <CommitInput
              className="thumb-person-label"
              value={portrait.label}
              placeholder="Name"
              maxLength={60}
              ariaLabel={`Who this is (${presenter ? "presenter" : "guest"})`}
              onCommit={(next) => session.setThumbPortraitLabel(portrait.id, next)}
            />
            {!fromLibrary && index > 0 && (
              <button
                type="button"
                className="thumb-person-promote"
                onClick={() => session.makeThumbPresenter(portrait.id)}
              >
                Make presenter
              </button>
            )}
            <button
              type="button"
              className="icon-btn thumb-person-remove"
              onClick={() => session.deleteThumbPortrait(portrait.id)}
              aria-label="Remove portrait"
              data-tip="Remove"
              data-tip-pos="top"
            >
              {iconClose}
            </button>
          </figure>
          );
        })}
        {room > 0 && (
          <label
            className={`thumb-person-add${uploading ? " busy" : ""}`}
            aria-label={portraits.length === 0 && !fromLibrary ? "Add a portrait for this board only" : "Add a guest"}
            data-tip={portraits.length === 0 && !fromLibrary ? "Add a portrait for this board only" : "Add a guest"}
            data-tip-pos="top"
          >
            {uploading ? (
              <span style={{ fontSize: 11 }}>…</span>
            ) : (
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                <path d="M8 3v10" />
                <path d="M3 8h10" />
              </svg>
            )}
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              disabled={uploading}
              onChange={(e) => {
                const files = [...(e.target.files ?? [])];
                e.target.value = ""; // let the same file be picked again
                void addFiles(files);
              }}
            />
          </label>
        )}
      </div>
    </section>
  );
}

/**
 * The presenter slot: the shared photo library. A fan of the first few
 * offered photos and the count, "Add photos" (the upload is everyone's, and
 * the server describes each photo as it lands), and "Library" for the whole
 * grid with each photo's description. Shown even while the library is empty,
 * because adding to it is the way in.
 */
function PresenterCard({ photos }: { photos: readonly PresenterPhoto[] }) {
  const uploads = useLibraryUploads();
  const [libraryOpen, setLibraryOpen] = useState(false);
  const all = usePresenterPhotos();
  const faces = photos.slice(0, 4);
  const undescribed = all.filter((photo) => photo.tags === undefined).length;
  return (
    <figure className="thumb-person presenter thumb-person-library">
      {faces.length > 0 ? (
        <div className="thumb-lib-faces" aria-hidden="true">
          {faces.map((photo) => (
            <img key={photo.id} src={assetUrl(photo.assetId)} alt="" />
          ))}
        </div>
      ) : (
        <div className="thumb-lib-faces thumb-lib-faces-empty" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
            <circle cx="8" cy="5.5" r="2.6" />
            <path d="M2.8 13.5c.7-2.7 2.6-4 5.2-4s4.5 1.3 5.2 4" />
          </svg>
        </div>
      )}
      <span className="thumb-person-role">Presenter</span>
      <span className="thumb-person-note">
        {photos.length > 0
          ? `AI picks from ${photos.length} ${photos.length === 1 ? "photo" : "photos"}`
          : undescribed > 0
            ? `${undescribed} ${undescribed === 1 ? "photo" : "photos"} waiting for a description`
            : "No shared photos yet"}
      </span>
      <div className="thumb-lib-actions">
        <AddPhotosButton uploads={uploads} atCap={false} className="thumb-link-btn" compact />
        <button type="button" className="thumb-link-btn" onClick={() => setLibraryOpen(true)}>
          Library
        </button>
      </div>
      {libraryOpen && <PresenterLibraryDialog onClose={() => setLibraryOpen(false)} />}
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/* Words                                                               */
/* ------------------------------------------------------------------ */

function WordsSection({ session, view }: { session: BoardSession; view: ThumbnailsView }) {
  const textLength = [...view.text].length;
  return (
    <section className="thumb-section">
      <div className="thumb-section-head">
        <span className="thumb-label">Words</span>
        <p className="thumb-hint">Leave blank and the script writes them.</p>
      </div>
      <div className="thumb-fields">
        <div className="thumb-field">
          <label className="thumb-field-label" htmlFor="thumb-title">
            Title
          </label>
          <CommitInput
            id="thumb-title"
            value={view.title}
            placeholder="Video title — optional"
            maxLength={THUMB_TITLE_MAX_LENGTH}
            onCommit={(next) => session.setThumbTitle(next)}
          />
        </div>
        <div className="thumb-field">
          <label className="thumb-field-label" htmlFor="thumb-text">
            On image
          </label>
          <div className="thumb-field-inline">
            <CommitInput
              id="thumb-text"
              value={view.text}
              placeholder="繁體中文 · 2 至 8 個字 — optional"
              maxLength={THUMB_TEXT_MAX_LENGTH}
              onCommit={(next) => session.setThumbText(next)}
            />
            {textLength > 0 && (
              <span
                className="thumb-field-meta"
                style={textLength > TEXT_IDEAL_MAX ? { color: "var(--color-warn-text)" } : undefined}
              >
                {textLength} 字
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Models                                                              */
/* ------------------------------------------------------------------ */

/**
 * The two picks are BOARD state, not a personal preference (they live in the
 * doc, unlike every other picker's localStorage pick) — a thumbnail run is a
 * shared artifact, and the whole board should agree on what rendered it. The
 * dropdowns are still the shared ones, so the models on offer here are the
 * models on offer everywhere.
 */
function ModelsSection({ session, view }: { session: BoardSession; view: ThumbnailsView }) {
  return (
    <section className="thumb-section">
      <div className="thumb-section-head">
        <span className="thumb-label">Models</span>
        <p className="thumb-hint">Shared with everyone on this board.</p>
      </div>
      <div className="thumb-fields">
        <div className="thumb-field">
          <label className="thumb-field-label" htmlFor="thumb-text-model">
            Hooks
          </label>
          <ModelSelect
            id="thumb-text-model"
            functionKey="thumbnail-concepts"
            kind="text"
            value={view.textModel ?? ""}
            onChange={(next) => session.setThumbTextModel(next === "" ? undefined : next)}
          />
        </div>
        <div className="thumb-field">
          <label className="thumb-field-label" htmlFor="thumb-image-model">
            Pictures
          </label>
          <ModelSelect
            id="thumb-image-model"
            functionKey="thumbnail-image"
            kind="image"
            value={view.imageModel ?? ""}
            onChange={(next) => session.setThumbImageModel(next === "" ? undefined : next)}
          />
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Gallery                                                             */
/* ------------------------------------------------------------------ */

interface CardContext {
  session: BoardSession;
  canvas: CanvasStore;
  ui: ThumbUiStore;
  view: ThumbnailsView;
  onShowGrid: (() => void) | undefined;
  /** Successive placements in one sitting are nudged apart (place.ts). */
  placed: { current: number };
}

function Gallery({
  session,
  canvas,
  ui,
  view,
  onShowGrid,
}: {
  session: BoardSession;
  canvas: CanvasStore;
  ui: ThumbUiStore;
  view: ThumbnailsView;
  onShowGrid: (() => void) | undefined;
}) {
  const placed = useRef(0);
  const ctx: CardContext = { session, canvas, ui, view, onShowGrid, placed };
  return (
    <>
      {view.runs.map((run, index) => (
        <Round key={run.runId} run={run} latest={index === 0} ctx={ctx} />
      ))}
    </>
  );
}

function Round({ run, latest, ctx }: { run: ThumbRunGroup; latest: boolean; ctx: CardContext }) {
  return (
    <section className="thumb-round" aria-label={latest ? "Latest round" : "Earlier round"}>
      <header className="thumb-round-head">
        <strong>{latest ? "Latest round" : "Earlier round"}</strong>
        <span>
          {run.columns.length} {run.columns.length === 1 ? "thumbnail" : "thumbnails"} ·{" "}
          {formatWhen(run.createdAt)}
        </span>
      </header>
      {run.columns.map((column) => (
        <Concept key={column.root.id} column={column} ctx={ctx} />
      ))}
    </section>
  );
}

function Concept({ column, ctx }: { column: ThumbConceptColumn; ctx: CardContext }) {
  const refining = useStore(ctx.ui, (s) => s.refining);
  const pendingUnder = [column.root, ...column.versions].filter((shot) => refining[shot.id] === true);
  return (
    <article className="thumb-concept">
      <ShotCard shot={column.root} ctx={ctx} />
      {(column.versions.length > 0 || pendingUnder.length > 0) && (
        <div className="thumb-concept-versions">
          {column.versions.map((shot) => (
            <ShotCard key={shot.id} shot={shot} ctx={ctx} />
          ))}
          {pendingUnder.map((shot) => (
            <figure className="thumb-card pending" key={`pending-${shot.id}`} aria-busy="true">
              <div className="thumb-card-frame">Refining…</div>
            </figure>
          ))}
        </div>
      )}
    </article>
  );
}

function ShotCard({ shot, ctx }: { shot: ThumbShotInfo; ctx: CardContext }) {
  const { session, canvas, ui, view, onShowGrid, placed } = ctx;
  const refineOpen = useStore(ui, (s) => s.refineOpenFor === shot.id);
  const refiningThis = useStore(ui, (s) => s.refining[shot.id] === true);
  const refineError = useStore(ui, (s) => s.refineErrors[shot.id] ?? null);
  const [placing, setPlacing] = useState(false);
  const picked = view.selectedId === shot.id;
  // Which library photo the face came from (F-134) — the fastest check that
  // the run picked the expression the hook wanted.
  const libraryPhotos = usePresenterPhotos();
  const libraryPhoto =
    shot.portraitId === undefined
      ? undefined
      : libraryPhotos.find((photo) => photo.id === shot.portraitId);
  // "On board" means the element is still there — a deleted element must not
  // keep a frame from being placed again.
  const onBoard = shot.elementId !== undefined && session.board.hasElement(shot.elementId);

  /**
   * Put the frame where the user can SEE it: show the grid first (a board open
   * as the script document has no canvas), wait for the canvas to know its
   * size, then centre the frame in the viewport.
   */
  async function useOnBoard() {
    setPlacing(true);
    try {
      onShowGrid?.();
      const { camera, viewport } = await waitForCanvas(canvas);
      const natural = { w: shot.w, h: shot.h };
      const rect = hasViewport(viewport)
        ? thumbPlacement(natural, camera, viewport, placed.current)
        : fallbackPlacement(natural, placed.current);
      const elementId = session.placeThumbOnBoard(shot, rect);
      if (elementId === null) {
        ui.getState().setError("You're offline — reconnect before placing a thumbnail.");
        return;
      }
      placed.current += 1;
      showToast("Thumbnail placed on the board and picked.");
    } finally {
      setPlacing(false);
    }
  }

  async function refine(feedback: string) {
    ui.getState().setRefineError(shot.id, null);
    ui.getState().setRefining(shot.id, true);
    ui.getState().setRefineOpen(null);
    try {
      await refineThumbnail(session.boardId, shot.id, feedback, view.imageModel);
    } catch (err) {
      ui.getState().setRefineError(shot.id, describeThumbnailError(err));
      ui.getState().setRefineOpen(shot.id);
    } finally {
      ui.getState().setRefining(shot.id, false);
    }
  }

  return (
    <figure className={`thumb-card${picked ? " picked" : ""}`}>
      <div className="thumb-card-frame">
        <img src={assetUrl(shot.assetId)} alt={shot.text} title={shot.prompt} />
        {(picked || onBoard) && (
          <div className="thumb-card-badges">
            {picked && <span className="thumb-badge picked">Picked</span>}
            {onBoard && <span className="thumb-badge">On board</span>}
          </div>
        )}
      </div>
      <figcaption className="thumb-card-caption">
        <strong className="thumb-card-text">{shot.text || "—"}</strong>
        {shot.feedback ? (
          <span className="thumb-card-feedback" title={shot.feedback}>
            “{shot.feedback}”
          </span>
        ) : (
          shot.title && (
            <span className="thumb-card-title" title={shot.title}>
              {shot.title}
            </span>
          )
        )}
        {libraryPhoto !== undefined && (
          <span className="thumb-card-photo" title={libraryPhoto.tags?.summary}>
            <img src={assetUrl(libraryPhoto.assetId)} alt="" />
            {libraryPhoto.tags?.mood || "library photo"}
          </span>
        )}
      </figcaption>
      <div className="thumb-card-actions">
        <button
          type="button"
          className={`btn${picked ? "" : " btn-primary"}`}
          onClick={() => void useOnBoard()}
          disabled={refiningThis || placing}
          title={onBoard ? "Place another copy on the board" : "Put this thumbnail on the board and pick it"}
        >
          {placing ? "Placing…" : onBoard ? "On board ✓" : "Use on board"}
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => ui.getState().setRefineOpen(refineOpen ? null : shot.id)}
          disabled={refiningThis}
          aria-expanded={refineOpen}
        >
          Refine
        </button>
        <span className="spacer" />
        <a
          className="btn btn-ghost"
          href={assetUrl(shot.assetId)}
          download={`thumbnail-${shot.concept || 0}-${shot.id}.jpg`}
          title="Save the image file"
        >
          Save
        </a>
      </div>
      {refineOpen && (
        <RefineBox
          error={refineError}
          onCancel={() => ui.getState().setRefineOpen(null)}
          onSubmit={(feedback) => void refine(feedback)}
        />
      )}
    </figure>
  );
}

/** "What should change?" — one sentence, Enter to send, Escape to close. */
function RefineBox({
  error,
  onCancel,
  onSubmit,
}: {
  error: string | null;
  onCancel: () => void;
  onSubmit: (feedback: string) => void;
}) {
  const [draft, setDraft] = useState("");
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  const ready = draft.trim().length > 0;

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // The board's own shortcuts must not see these keystrokes.
    event.stopPropagation();
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
    } else if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      if (ready) onSubmit(draft.trim());
    }
  }

  return (
    <div className="thumb-refine">
      <textarea
        ref={ref}
        value={draft}
        placeholder="What should change? — e.g. 字再大啲、背景轉藍色、笑容誇張啲"
        rows={2}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        aria-label="What should change"
      />
      {error !== null && (
        <p className="thumb-error" role="alert">
          {error}
        </p>
      )}
      <div className="thumb-refine-actions">
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!ready}
          onClick={() => onSubmit(draft.trim())}
        >
          Refine
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Small pieces                                                        */
/* ------------------------------------------------------------------ */

/**
 * A single-line input that edits locally and writes to the doc on blur (or
 * Enter). Re-syncs from `value` when the doc changes underneath — a teammate's
 * edit lands live — EXCEPT while this user is typing in it.
 */
function CommitInput({
  id,
  className,
  value,
  placeholder,
  maxLength,
  ariaLabel,
  onCommit,
}: {
  id?: string;
  className?: string;
  value: string;
  placeholder: string;
  maxLength: number;
  ariaLabel?: string;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);

  const commit = () => {
    if (draft !== value) onCommit(draft);
  };

  return (
    <input
      id={id}
      type="text"
      className={className}
      value={draft}
      placeholder={placeholder}
      maxLength={maxLength}
      aria-label={ariaLabel}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter" && !e.nativeEvent.isComposing) {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

function formatWhen(at: number): string {
  const seconds = Math.round((Date.now() - at) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
